import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import JsPDF from "jspdf";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import DataTableComponentBackendPagination from "../../common/DataTableComponentBackendPagination";
import Button from "../../library/Button";
import { TABLE_DATE_FORMAT } from "../../../utils/Constants";
import {
    GetFirstAidBoxList,
    GetFirstAidBoxListFilters,
    DeleteFirstAidBox,
    GetFirstAidBoxQrCode
} from "../../../services/FirstAidBoxService";
import { invalidateFirstAidBoxQueries, useOhsAccess } from "../../../query/hooks/QueryHooksFirstAidBox";
import { OHS_BASE_PATH, PERFORM_CHECK_TOOLTIP, PERFORM_MANAGER_CHECK_TOOLTIP } from "./ohsConstants";
import { MutedText } from "./Ohs.styles";
import ConditionBadges from "./ConditionBadges";
import OhsUserCell from "./OhsUserCell";
import { getPrefixedUser } from "./ohsUtils";
import ButtonGroup from "../../library/ButtonGroup";

/**
 * Boxes tab - every box, auto-filtered to the user's own (assigned or alternate) boxes when they
 * have any, with a toggle to see all of them.
 */
function BoxList() {
    const history = useHistory();
    const { data: ohsAccess } = useOhsAccess();
    const [filterOptions, setFilterOptions] = useState({});
    const [myBoxesOnly, setMyBoxesOnly] = useState(false);
    const [hasAppliedDefault, setHasAppliedDefault] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (hasAppliedDefault || filterOptions.hasAssignedBoxes === undefined) {
            return;
        }
        setMyBoxesOnly(filterOptions.hasAssignedBoxes);
        setHasAppliedDefault(true);
    }, [filterOptions.hasAssignedBoxes, hasAppliedDefault]);

    const additionalParams = useMemo(() => ({ myBoxesOnly }), [myBoxesOnly]);

    /**
     * Builds and downloads the A4 poster printed and stuck inside a box - its QR code opens the
     * box's "log items used" screen.
     * @param {Object} box Box row.
     */
    const handleDownloadQrPoster = async (box) => {
        try {
            const { data } = await GetFirstAidBoxQrCode(box.id, window.location.origin);
            const boxQrCode = data.data;
            const poster = new JsPDF({ unit: "mm", format: "a4" });
            const pageWidth = poster.internal.pageSize.getWidth();
            const qrSize = 120;

            poster.setFontSize(26);
            poster.text("First Aid Box", pageWidth / 2, 30, { align: "center" });
            poster.setFontSize(20);
            poster.text(boxQrCode.box_code, pageWidth / 2, 42, { align: "center" });
            poster.setFontSize(12);
            poster.text(`${boxQrCode.campus_name} · ${boxQrCode.building_name} · ${boxQrCode.area_location}`, pageWidth / 2, 52, { align: "center" });
            poster.addImage(boxQrCode.qrCodeDataUrl, "PNG", (pageWidth - qrSize) / 2, 62, qrSize, qrSize);
            poster.setFontSize(14);
            poster.text("Used something from this box?", pageWidth / 2, 198, { align: "center" });
            poster.setFontSize(12);
            poster.text(
                [
                    "1. Scan this code with your phone and log in to AMI.",
                    "2. Enter the serial on the tag you removed and on the new tag you fitted.",
                    "3. Enter how many of each item you used.",
                ],
                pageWidth / 2,
                208,
                { align: "center", lineHeightFactor: 1.6 }
            );
            poster.save(`First_Aid_Box_${boxQrCode.box_code}_QR.pdf`);
        } catch (error) {
            console.log("BoxList.js->handleDownloadQrPoster->Error", error);
            Swal.fire("Error", "The QR poster could not be created.", "error");
        }
    };

    const handleDelete = async (box) => {
        try {
            const confirmation = await Swal.fire({
                title: "Are you sure?",
                text: `Box ${box.box_code} will be deleted. Its check history is kept for auditing.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, delete it!",
            });
            if (!confirmation.isConfirmed) {
                return;
            }

            await DeleteFirstAidBox(box.id);
            await invalidateFirstAidBoxQueries();
            Swal.fire("Deleted!", "The box has been deleted.", "success");
            setReloadKey((currentKey) => currentKey + 1);
        } catch (error) {
            console.log("BoxList.js->handleDelete->Error", error);
            Swal.fire("Error", "The box could not be deleted.", "error");
        }
    };

    const columns = useMemo(
        () => [
            {
                name: "Box",
                selector: "box_code",
                sortField: "box_code",
                sortable: true,
                minWidth: "140px",
                cell: (row) => (
                    <Link to={`${OHS_BASE_PATH}/boxes/open/${row.id}`} className="curser feature-name" title={row.box_code}>
                        <span className="textLimit100">{row.box_code}</span>
                    </Link>
                ),
            },
            {
                name: "Campus",
                selector: "campus_name",
                sortField: "campus_name",
                sortable: true,
                minWidth: "130px",
                cell: (row) => row.campus_name || "-",
            },
            {
                name: "Building",
                selector: "building_name",
                sortField: "building_name",
                sortable: true,
                minWidth: "140px",
                cell: (row) => <span className="textLimit100" title={row.building_name}>{row.building_name || "-"}</span>,
            },
            {
                name: "Area",
                selector: "area_location",
                sortField: "area_location",
                sortable: true,
                minWidth: "140px",
                cell: (row) => <span className="textLimit100" title={row.area_location}>{row.area_location}</span>,
            },
            {
                name: "First Aider",
                selector: "assigned_name",
                sortField: "assigned_name",
                sortable: true,
                minWidth: "180px",
                cell: (row) => {
                    const assignedFirstAider = getPrefixedUser(row, "assigned", row.assigned_first_aider_id);
                    const alternateFirstAider = getPrefixedUser(row, "alternate", row.alternate_first_aider_id);
                    return (
                        <div>
                            {assignedFirstAider ? <OhsUserCell user={assignedFirstAider} /> : <MutedText>Unassigned</MutedText>}
                            {alternateFirstAider && (
                                <>
                                    <MutedText>Alternate</MutedText>
                                    <OhsUserCell user={alternateFirstAider} />
                                </>
                            )}
                        </div>
                    );
                },
            },
            {
                name: "Last Checked",
                selector: "last_checked_at",
                sortField: "last_checked_at",
                sortable: true,
                minWidth: "130px",
                cell: (row) => (row.last_checked_at ? moment(row.last_checked_at).format(TABLE_DATE_FORMAT) : "Never"),
            },
            {
                name: "Condition",
                selector: "last_check_condition",
                sortField: "last_check_condition",
                sortable: true,
                minWidth: "140px",
                cell: (row) => <ConditionBadges statuses={row.statuses || [row.last_check_condition]} />,
            },
            {
                name: "Action",
                width: "110px",
                right: true,
                cell: (row) => {
                    // First aiders only ever get Perform Check - on any box, so they can cover a colleague on leave.
                    if (!ohsAccess.canManage) {
                        return (
                            <div className="assessment-08 btn-dropdown-grp">
                                <div className="as-buttons">
                                    <button
                                        type="button"
                                        className="btn btn-primary rounded-circle"
                                        title={PERFORM_CHECK_TOOLTIP}
                                        onClick={() => history.push(`${OHS_BASE_PATH}/boxes/check/${row.id}`)}
                                    >
                                        <i className="fal fa-clipboard-check"></i>
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="assessment-08 btn-dropdown-grp">
                            <div className="as-buttons d-flex">
                                <button
                                    type="button"
                                    className="btn btn-primary rounded-circle"
                                    title="Open"
                                    onClick={() => history.push(`${OHS_BASE_PATH}/boxes/open/${row.id}`)}
                                >
                                    <i className="fal fa-folder-open"></i>
                                </button>
                                <div className="dropdown btn-dropdown-item">
                                    <button
                                        className="btn btn-primary rounded-circle dropdown-toggle"
                                        type="button"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        title="More"
                                    >
                                        <i className="fal fa-ellipsis-h-alt"></i>
                                    </button>
                                    <div className="dropdown-menu">
                                        <button
                                            type="button"
                                            className="btn btn-primary rounded-circle"
                                            title={PERFORM_CHECK_TOOLTIP}
                                            onClick={() => history.push(`${OHS_BASE_PATH}/boxes/check/${row.id}`)}
                                        >
                                            <i className="fal fa-clipboard-check"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary rounded-circle"
                                            title={PERFORM_MANAGER_CHECK_TOOLTIP}
                                            onClick={() => history.push(`${OHS_BASE_PATH}/boxes/check/${row.id}?managerCheck=true`)}
                                        >
                                            <i className="fal fa-user-shield"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary rounded-circle"
                                            title="Download QR Poster - print it and stick it inside the box so whoever uses something can log it"
                                            onClick={() => handleDownloadQrPoster(row)}
                                        >
                                            <i className="fal fa-qrcode"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger rounded-circle"
                                            title="Delete"
                                            onClick={() => handleDelete(row)}
                                        >
                                            <i className="fal fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
        ],
        [history, ohsAccess.canManage]
    );

    const filterConfig = [
        { key: "viaCampus", label: "Campus", optionsKey: "CampusFilter" },
        { key: "viaBuilding", label: "Building", optionsKey: "BuildingFilter", isSearchFilter: true },
        { key: "viaFirstAider", label: "First Aider", optionsKey: "FirstAiderFilter", isSearchFilter: true },
        { key: "viaCondition", label: "Condition", optionsKey: "ConditionFilter" },
        { key: "viaCheckStatus", label: "This Month", optionsKey: "CheckStatusFilter" },
    ];

    return (
        <DataTableComponentBackendPagination
            key={reloadKey}
            columns={columns}
            getDataApi={GetFirstAidBoxList}
            getFiltersApi={GetFirstAidBoxListFilters}
            setFilterData={setFilterOptions}
            fileName="First_Aid_Boxes"
            filterConfig={filterConfig}
            showExport={true}
            storeFilters={true}
            storageKey="FirstAidBoxList"
            showFilter={true}
            showSearch={true}
            defaultSortField="box_code"
            defaultSortAsc={true}
            additionalParams={additionalParams}
            LoadingComponent={<SkeletonTicketList />}
            tableButton={[
                <ButtonGroup key="box-list-actions">
                    {filterOptions.hasAssignedBoxes && (
                        <Button
                            variant={myBoxesOnly ? "primary" : "secondary"}
                            onClick={() => setMyBoxesOnly((currentValue) => !currentValue)}
                            title={myBoxesOnly ? "Showing boxes you are the assigned or alternate first aider for" : "Showing every box"}
                        >
                            <i className="fal fa-user-check"></i>
                            {myBoxesOnly ? "My Boxes Only" : "All Boxes"}
                        </Button>
                    )}
                    {ohsAccess.canManage && (
                        <Button
                            variant="primary"
                            onClick={() => history.push(`${OHS_BASE_PATH}/boxes/open`)}
                            title="Add box"
                        >
                            <i className="fal fa-plus"></i>
                            Add Box
                        </Button>
                    )}
                </ButtonGroup>,
            ]}
        />
    );
}

export default BoxList;
